/**
 * Utility functions for detecting whether Canvas App resources were loaded from browser cache
 * using the Resource Timing API.
 */

/**
 * Determines if a resource was loaded from cache based on Resource Timing API data
 */
const isResourceFromCache = (entry: PerformanceResourceTiming): boolean => {
  // If transferSize is 0, the resource was definitely served from cache
  if (entry.transferSize === 0) {
    return true;
  }

  // If transferSize is significantly smaller than encodedBodySize,
  // it's likely from cache (accounting for headers and small differences)
  if (entry.encodedBodySize > 0 && entry.transferSize > 0) {
    const ratio = entry.transferSize / entry.encodedBodySize;
    // If transfer size is less than 10% of encoded size, likely from cache
    // This accounts for cases where some headers are still transferred
    return ratio < 0.1;
  }

  // If we can't determine, assume not from cache (conservative approach)
  return false;
};

const isCanvasAppNodeModulesResource = (
  url: string,
  canvasAppOrigin: string
): boolean => url.startsWith(canvasAppOrigin) && url.includes('vendors');

const isCanvasAppMainResource = (
  url: string,
  canvasAppOrigin: string
): boolean =>
  url.startsWith(canvasAppOrigin) &&
  url.includes('canvas-app') &&
  !url.includes('chunk-ep');

/**
 * Cache detection information for Canvas App resources
 */
type CanvasAppCacheInfo = {
  canvasAppNodeModulesCached?: boolean;
  canvasAppMainCached?: boolean;
};

/**
 * Detects cache usage for Canvas App resources using the Resource Timing API
 */
export const detectCanvasAppBundlesCacheUsage = (
  canvasAppOrigin: string
): CanvasAppCacheInfo => {
  try {
    // Get all resource timing entries
    const resourceEntries = performance.getEntriesByType(
      'resource'
    ) as PerformanceResourceTiming[];

    return resourceEntries.reduce(
      (acc, entry) => {
        if (isCanvasAppNodeModulesResource(entry.name, canvasAppOrigin)) {
          acc.canvasAppNodeModulesCached = isResourceFromCache(entry);
        }

        if (isCanvasAppMainResource(entry.name, canvasAppOrigin)) {
          acc.canvasAppMainCached = isResourceFromCache(entry);
        }

        return acc;
      },
      {
        canvasAppNodeModulesCached: undefined,
        canvasAppMainCached: undefined,
      } as CanvasAppCacheInfo
    );
  } catch (error) {
    return {
      canvasAppNodeModulesCached: undefined,
      canvasAppMainCached: undefined,
    };
  }
};
